/* eslint-disable max-classes-per-file */
import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Injectable,
  Input,
  OnDestroy,
  Optional,
  Output,
  Provider,
  Type
} from '@angular/core';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import { ConnectableObservable, fromEvent, Observable, ReplaySubject } from 'rxjs';
import { filter, map, publishReplay, share, take, takeUntil, tap, throwIfEmpty, withLatestFrom } from 'rxjs/operators';
import { apParseUrl } from 'webcommon/legacy-common';
import { ApBaseClass, Destroyable$, logColor, logInDevMode_NotTests } from 'webcommon/shared';

@Directive({
  selector: 'iframe[apIframe]',
  host: {
    '[src]': 'safeUrl',
  },
})
export class ApIframeDirective {
  static readonly defaultSrc = 'about:blank';

  private _url: string = ApIframeDirective.defaultSrc;
  @Input()
  get url(): string {
    return this._url;
  }
  set url(val: string | null | undefined) {
    if (this._url !== val) {
      this._url = val || ApIframeDirective.defaultSrc;
      // Set safeUrl here because the host binding expression can't contain pipes
      this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this._url);
    }
  }

  safeUrl: SafeResourceUrl = this.url;

  constructor(
    private readonly sanitizer: DomSanitizer,
    private readonly elementRef: ElementRef<HTMLIFrameElement>,
    @Optional() private readonly apIframeDelegate: ApIframeDelegate | null,
  ) {
    if (this.apIframeDelegate) {
      this.apIframeDelegate.iframeReady(this.elementRef.nativeElement);
    }
  }
}





// provide this class (or a subclass) to easily communicate with iframes,
// and make sure the proper validation is done on outgoing and incoming messages
@Injectable()
export class ApIframeDelegate extends ApBaseClass implements OnDestroy {
  private readonly iframeConfig$ = new ReplaySubject<ApIframeConfig>(1);
  readonly windowMessages$ = fromEvent<MessageEvent>(window, 'message').pipe(
    withLatestFrom(this.iframeConfig$),
    filter(value => isValidWindowMessage(value[0], value[1])),
    map(value => value[0]),
    share()
  );

  // shortcut to provide a subclass of ApIframeDelegate as itself, and as ApIframeDelegate
  static provideSubClass(val: Type<ApIframeDelegate>): Provider[] {
    return [
      val,
      {
        provide: ApIframeDelegate,
        useExisting: val,
      }
    ];
  }

  override ngOnDestroy() {
    this.iframeConfig$.complete();
    super.ngOnDestroy();
  }

  async tryPostMessageToFrame(message: any): Promise<void> {
    const iframeConfig = await getIframeConfigP(this.iframeConfig$);
    iframeConfig.expectedSource.postMessage(message, {
      targetOrigin: iframeConfig.expectedOrigin,
    });
    logInDevMode_NotTests(() => [...logColor(`Sent window.postMessage`, 'magenta'), message,
      `with targetOrigin`, iframeConfig.expectedOrigin
    ]);
  }

  // called by iframe directive after the element is available
  iframeReady(element: HTMLIFrameElement): void {
    getIframeConfig$(element, this).pipe(
      tap((value) => this.iframeConfig$.next(value)),
    ).subscribe();
  }

  // get a new promise for the next window message, that also matches the filterFn
  getNextWindowMessageP<TMessageEvent extends MessageEvent>(
    filterFn: (event: MessageEvent) => event is TMessageEvent,
  ): Promise<TMessageEvent> {
    return this.windowMessages$.pipe(
      throwIfEmpty(() => new Error('ApIframeDelegate: a value was never emitted from windowMessages$ before complete()')),
      filter(filterFn),
      take(1),
    ).toPromise();
  }
}





// the configuration of the iframe to communicate with:
// for sending messages, and validating the received ones
export interface ApIframeConfig {
  readonly expectedOrigin: string;
  readonly expectedSource: Window;
}


// destroyable$ should normally be the current component
// (the one that needs to be tracked to know when to cleanup this ConnectableObservable)
//
// example values:
// iframe.src = 'https://terminology-qa-pop.e-mds.com/?k=c0cbaaae-0cb8-41f2-acd6-5ae8d56f91d5'
// eslint-disable-next-line max-len
// ApIframeDirective.safeUrl = SafeResourceUrlImpl {changingThisBreaksApplicationSecurity: 'https://terminology-qa-pop.e-mds.com/?k=c0cbaaae-0cb8-41f2-acd6-5ae8d56f91d5'}
// ApIframeDirective.url = 'https://terminology-qa-pop.e-mds.com/?k=c0cbaaae-0cb8-41f2-acd6-5ae8d56f91d5'
export function getIframeConfig$(element: HTMLIFrameElement, destroyable$: Destroyable$): Observable<ApIframeConfig> {
  // https://rxjs.dev/deprecations/multicasting
  // multicast to a ConnectableObservable, so that we can listen for events right away,
  // even before there are subscribers
  const iframeConfig$ = fromEvent(element, 'load').pipe(
    map((_value) => {
      return {
        expectedOrigin: getExpectedOrigin(element),
        expectedSource: element.contentWindow,
      } as const;
    }),
    filter((value): value is ApIframeConfig => {
      return !!value.expectedOrigin && !!value.expectedSource;
    }),
    // maybe this takeUntil doesn't matter, but you won't get a complete event without it....
    // OR another option is multicasting thru a separate created Subject or ReplaySubject, and then completing that
    takeUntil(destroyable$.onDestroy$),
    publishReplay(1),
  ) as ConnectableObservable<ApIframeConfig>;

  // start it
  const connectSub = iframeConfig$.connect();

  destroyable$.onDestroy$.subscribe(() => {
    // clean-up the ConnectableObservable
    connectSub.unsubscribe();
  });

  return iframeConfig$;
}


// https://stackoverflow.com/a/58091944/5335355
// https://html.spec.whatwg.org/multipage/browsers.html#concept-origin-opaque
const opaqueOrigin = 'null';


// get the expected origin for incoming and outgoing messages to this iframe
export function getExpectedOrigin(element: HTMLIFrameElement): string {
  const elementOrigin = apParseUrl(element.src)?.origin;
  // it's not clear exactly what the difference would be here between self.origin and location.origin
  // We need to add tests to cover these cases, if there are actually meaningful differences
  return elementOrigin && elementOrigin !== opaqueOrigin ? elementOrigin : self.origin;
}



export function getIframeConfigP(iframeConfig$: Observable<ApIframeConfig>): Promise<ApIframeConfig> {
  return iframeConfig$.pipe(
    throwIfEmpty(() => new Error(`getIframeConfigP: a value was never emitted from iframeConfig$ before complete()`)),
    take(1),
  ).toPromise();
}


// filter to only MessageEvents that we trust
export function isValidWindowMessage(event: MessageEvent, iframeConfig: ApIframeConfig): boolean {
  logInDevMode_NotTests(() => [...logColor(`Validating Received window.postMessage`, 'magenta'), event.data, event]);

  // Return if the origin doesn't match what we expect/trust
  // https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage#notes
  if (!iframeConfig.expectedOrigin || event.origin !== iframeConfig.expectedOrigin) {
    return false;
  }
  // Return if the source doesn't match what we expect/trust
  if (!iframeConfig.expectedSource || event.source !== iframeConfig.expectedSource) {
    return false;
  }
  return true;
}



@Directive({
  selector: 'iframe[apSafeWindowMessage]',
  standalone: true,
})
export class ApSafeWindowMessageDirective extends ApBaseClass {
  private iframeConfig$ = getIframeConfig$(this.elementRef.nativeElement, this);

  @Output() apSafeWindowMessage = new EventEmitter<MessageEvent>(true);

  constructor(
    private elementRef: ElementRef<HTMLIFrameElement>,
  ) {
    super();
  }

  @HostListener('window:message', ['$event'])
  protected async onPostMessage(event: MessageEvent) {
    const iframeConfig = await getIframeConfigP(this.iframeConfig$);
    if (!isValidWindowMessage(event, iframeConfig)) {
      return;
    }

    this.apSafeWindowMessage.emit(event);
  }
}
